import { Col, Container, View } from "@amzn/stencil-react-components/layout";
import { H1 } from "@amzn/stencil-react-components/text";
import { TabBar, TabPanel, TabSwitcher, useTabs } from "@amzn/stencil-react-components/tabs";
import { Spinner, SpinnerSize } from "@amzn/stencil-react-components/spinner";
import { MessageBanner, MessageBannerType } from "@amzn/stencil-react-components/message-banner";
import React, { FC, ReactNode, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { getString } from "@/common/uistringlabels/ui-string-utils";
import { useHssStatus } from "@/common/hooks/use-hss-status";
import type { HssStatusResponse } from "@/services/hardware-self-service/hardware-self-service.types";
import { useDocumentTitle } from "@/common/hooks/use-document-title";
import { UserPermissions } from "@/services/user-permissions/user-permissions";
import { CAMPAIGN_TABS } from "./campaigns.constants";
import { needsManagerAttention } from "./campaigns.util";
import { parseLinkedReport, shouldDropLinkedReport } from "./campaigns-deep-link";
import { BaselineSummary } from "./components/baseline-summary";
import { MyBaseliningPanel } from "./components/my-baselining-panel";
import { TeamCampaignsSection } from "./components/team-campaigns-section";

interface CampaignsPageLayoutProps {
  children: ReactNode;
}

const CampaignsPageLayout: FC<CampaignsPageLayoutProps> = ({ children }) => (
  <Container isFullWidth={false}>
    <View padding="S600">{children}</View>
  </Container>
);

const CampaignsSpinner: FC = () => (
  <CampaignsPageLayout>
    <View display="flex" justifyContent="center">
      <Spinner size={SpinnerSize.Medium} />
    </View>
  </CampaignsPageLayout>
);

interface CampaignsTabsProps {
  status?: HssStatusResponse;
  linkedAlias?: string;
  currentUsername: string;
}

/** Mounted only once the status has loaded, because `useTabs` reads `defaultTab` only on mount. */
const CampaignsTabs: FC<CampaignsTabsProps> = ({ status, linkedAlias, currentUsername }) => {
  const directReports = status?.directReports ?? [];
  const showsTeamTab = Boolean(linkedAlias) || directReports.some(needsManagerAttention);

  const tabs = [
    { value: CAMPAIGN_TABS.myBaselining, label: getString("campaigns.myBaseliningTab") },
    ...(showsTeamTab
      ? [{ value: CAMPAIGN_TABS.myTeamBaselining, label: getString("campaigns.myTeamBaseliningTab") }]
      : []),
  ];
  const { tabBarProps, tabSwitcherProps } = useTabs({
    tabs,
    defaultTab: linkedAlias ? CAMPAIGN_TABS.myTeamBaselining : CAMPAIGN_TABS.myBaselining,
  });

  return (
    <CampaignsPageLayout>
      <Col gridGap="S400" width="100%">
        <Col gridGap="S200">
          <H1>{getString("campaigns.pageHeading")}</H1>
          <BaselineSummary />
          <TabBar {...tabBarProps} />
        </Col>
        <TabSwitcher {...tabSwitcherProps} shouldUnmountHiddenPanels>
          <TabPanel value={CAMPAIGN_TABS.myBaselining}>
            <MyBaseliningPanel status={status?.myStatus} currentUsername={currentUsername} />
          </TabPanel>
          {showsTeamTab && (
            <TabPanel value={CAMPAIGN_TABS.myTeamBaselining}>
              <TeamCampaignsSection
                directReports={directReports}
                currentUsername={currentUsername}
                linkedAlias={linkedAlias}
                isTeamListTruncated={status?.truncated}
              />
            </TabPanel>
          )}
        </TabSwitcher>
      </Col>
    </CampaignsPageLayout>
  );
};

const CampaignsPage: FC = () => {
  const { data, isLoading, error } = useHssStatus();
  const { pathname, search } = useLocation();
  const history = useHistory();
  const currentUsername = UserPermissions.current.username ?? "";
  const linkedAlias = parseLinkedReport(search);
  const shouldDropLink =
    linkedAlias !== undefined && data !== undefined && shouldDropLinkedReport(linkedAlias, data, currentUsername);

  useEffect(() => {
    if (shouldDropLink) {
      history.replace(pathname);
    }
  }, [shouldDropLink, history, pathname]);

  useDocumentTitle(getString("campaigns.pageHeading"));

  if (isLoading || shouldDropLink) {
    return <CampaignsSpinner />;
  }

  if (error) {
    return (
      <CampaignsPageLayout>
        <MessageBanner type={MessageBannerType.Error} isDismissible={false}>
          {getString("campaigns.error")}
        </MessageBanner>
      </CampaignsPageLayout>
    );
  }

  return <CampaignsTabs status={data} linkedAlias={linkedAlias} currentUsername={currentUsername} />;
};

export default CampaignsPage;
